import * as Icon from "@ant-design/icons";
import { Tag, Modal, Form, Row, Col, Flex } from "antd";
import { ColumnsType, TableProps } from "antd/es/table";
import { FilterValue } from "antd/es/table/interface";
import Search from "antd/lib/input/Search";
import { useState, useEffect } from "react";
import { AtivoModel } from "../../models/ativoModel";
import { FormaPagamentoRecebimentoModel } from "../../models/formaPagamentoRecebimento/formaPagamentoRecebimentoModel";
import { setTitle } from "../../redux/slices/configuracoes/configuracoesSlice";
import { atualizarFormaPagamentoRecebimentoAtivo, buscarFormaPagamentoRecebimento, deletarFormaPagamentoRecebimento } from "../../services/formaPagamentoRecebimentoService";
import { useAppSelector, useAppDispatch } from "../../util/hooks";
import CadastroFormaPagamentoRecebimento from "./modals/cadastrar";
import {
  setBuscaFormaPagamentoRecebimento,
  setEntityFormaPagamentoRecebimento,
  setModalCadastrarFormaPagamentoRecebimento,
  setModalDeletarFormaPagamentoRecebimento,
} from "../../redux/slices/formaPagamentoRecebimento/formaPagamentoRecebimentoSlice";
import { ItemMenuAcoes } from "../../models/sistema/itemMenuAcoes";
import AcoesComponent from "../../components/table/acoesComponent";
import { ButtonComponent } from "../../components/button/cadastrar";
import MensagemModal from "../../components/modal/mensagem";
import TableComponent from "../../components/table/tableComponent";
import { SwitchComponent } from "../../components/button/switch";

const FormaPagamentoRecebimento = () => {
  //estados hlobais
  const formaPagamentoRecebimentoState = useAppSelector((state) => state.formaPagamentoRecebimento);

  //hooks
  const dispatch = useAppDispatch();

  //estados locais
  const [form] = Form.useForm();
  const [filteredInfo, setFilteredInfo] = useState<Record<string, FilterValue | null | string>>({});

  const editar = Object.values(formaPagamentoRecebimentoState.entity).length !== 0;

  useEffect(() => {
    dispatch(setTitle("FORMA DE PAGAMENTO"));
    dispatch(buscarFormaPagamentoRecebimento(formaPagamentoRecebimentoState.busca));
    form.setFieldsValue({
      pesquisar: formaPagamentoRecebimentoState.busca,
    });
  }, []);

  const goCadastrar = () => {
    dispatch(setEntityFormaPagamentoRecebimento({}));
    dispatch(setModalCadastrarFormaPagamentoRecebimento(true));
  };

  const goEditar = (value: FormaPagamentoRecebimentoModel) => {
    dispatch(setEntityFormaPagamentoRecebimento(value));
    dispatch(setModalCadastrarFormaPagamentoRecebimento(true));
  };

  const goDeletar = (value: FormaPagamentoRecebimentoModel) => {
    dispatch(setEntityFormaPagamentoRecebimento(value));
    dispatch(setModalDeletarFormaPagamentoRecebimento(true));
  };

  const closeModalCadastrar = () => {
    dispatch(setModalCadastrarFormaPagamentoRecebimento(false));
  };

  const closeModalDeletar = () => {
    dispatch(setModalDeletarFormaPagamentoRecebimento(false));
  };

  const onSearch = (value: string) => {
    dispatch(setBuscaFormaPagamentoRecebimento(value));
    dispatch(buscarFormaPagamentoRecebimento(value));
  };

  const onChangeTable: TableProps<FormaPagamentoRecebimentoModel>["onChange"] = (pagination, filters, sorter, extra) => {
    setFilteredInfo(filters);
  };

  const onChangeAtivo = (entity: FormaPagamentoRecebimentoModel) => {
    let args: AtivoModel = {
      id: entity.id,
      ativo: !entity.ativo,
    };
    dispatch(atualizarFormaPagamentoRecebimentoAtivo({ value: args, busca: formaPagamentoRecebimentoState.busca }));
  };

  const items: ItemMenuAcoes[] = [
    {
      label: "Editar",
      key: "1",
      icon: <Icon.EditOutlined />,
      function: (item: any) => goEditar(item),
    },
    {
      label: "Remover",
      key: "2",
      icon: <Icon.DeleteOutlined />,
      function: (item: any) => goDeletar(item),
    },
  ];

  const columns: ColumnsType<FormaPagamentoRecebimentoModel> = [
    {
      title: "FORMA DE PAGAMENTO",
      dataIndex: "denominacao",
      width: 100,
      sorter: (a, b) => a.denominacao.localeCompare(b.denominacao),
      sortDirections: ["ascend", "descend", "ascend"],
    },
    {
      title: "DESCRIÇÃO",
      dataIndex: "descricao",
      width: 100,
    },
    {
      title: "CATEGORIA",
      width: 100,
      align: "center",
      filters: [
        {
          text: "Receita",
          value: "receita" as any,
        },
        {
          text: "Despesa",
          value: "despesa" as any,
        },
      ],
      onFilter: (_, record: any) => {
        let verifica: any = Object.keys(filteredInfo).length;
        if (verifica !== 0) {
          let chaves: any = Object.values(filteredInfo)[0];
          if (chaves !== null) {
            if (chaves.length === 2) {
              return record.despesa || record.receita;
            } else {
              return chaves[0] === "despesa" ? record.despesa : record.receita;
            }
          } else {
            return record;
          }
        }
        return record.receita;
      },
      render: (_, record) => (
        <Flex justify="center" gap={5}>
          {record.receita && <Tag>Receita</Tag>}
          {record.despesa && <Tag>Despesa</Tag>}
        </Flex>
      ),
    },
    {
      title: "ATIVO",
      dataIndex: "ativo",
      width: 100,
      align: "center",
      filters: [
        {
          text: "Ativo",
          value: "true" as any,
        },
        {
          text: "Inativo",
          value: "false" as any,
        },
      ],
      onFilter: (value: any, record: any) => record.ativo.toString().includes(value),
      defaultFilteredValue: ["true" as any],
      render: (_, record) => <SwitchComponent onChange={() => onChangeAtivo(record)} checked={record.ativo} />,
    },
    {
      title: "AÇÕES",
      dataIndex: "acoes",
      align: "center",
      width: 75,
      render: (_, record) => <AcoesComponent record={record} items={items} />,
    },
  ];

  return (
    <Flex vertical>
      <Form layout="vertical" form={form}>
        <Row gutter={12} align="bottom">
          <Col span={20}>
            <Flex gap={12}>
              <Form.Item label="Pesquisar" name="pesquisar">
                <Search allowClear placeholder="Forma de pagamento ou Descrição" onSearch={onSearch} className="searchInput" enterButton style={{ width: 400 }} />
              </Form.Item>
            </Flex>
          </Col>
          <Col span={4}>
            <Flex justify="end">
              <ButtonComponent onClick={goCadastrar} />
            </Flex>
          </Col>
        </Row>
      </Form>
      <TableComponent
        data={formaPagamentoRecebimentoState.list}
        columns={columns}
        loading={formaPagamentoRecebimentoState.loading}
        tableProps={{
          className: "tableCentroCusto",
          onChange: onChangeTable,
        }}
      />
      <Modal
        title={editar ? "EDITAR FORMA DE PAGAMENTO" : "NOVA FORMA DE PAGAMENTO"}
        centered
        width={850}
        open={formaPagamentoRecebimentoState.modalCadastrar}
        onCancel={closeModalCadastrar}
        footer={null}
        destroyOnHidden
      >
        <CadastroFormaPagamentoRecebimento />
      </Modal>
      <Modal title={"REMOVER FORMA DE PAGAMENTO"} centered width={350} open={formaPagamentoRecebimentoState.modalDeletar} onCancel={closeModalDeletar} footer={null} destroyOnHidden>
        <MensagemModal
          text="Deseja remover a forma de pagamento?"
          action={deletarFormaPagamentoRecebimento({ value: formaPagamentoRecebimentoState.entity.id, busca: formaPagamentoRecebimentoState.busca })}
          close={setModalDeletarFormaPagamentoRecebimento(false)}
        />
      </Modal>
    </Flex>
  );
};

export default FormaPagamentoRecebimento;
